<template>
  <div class="hris-page">
    <div class="hris-page-inner">
      <div class="hris-page-header">
        <span class="hris-page-eyebrow">Onboarding</span>
        <h1 class="hris-page-title">Welcome to Your Onboarding Portal</h1>
        <p class="hris-page-subtitle">
          Please complete all required checklist items, review orientation times, and check your employment contract status below.
        </p>
      </div>

      <div class="row q-col-gutter-md">
        <!-- CHECKLIST MODULE -->
        <div class="col-12 col-md-7">
          <div class="hris-panel full-height">
            <h3 class="hris-panel-title">
              <i class="fas fa-check-circle q-mr-sm"></i>My Checklist Tasks
            </h3>
            <q-table
              :data="checklists"
              :columns="columns"
              row-key="id"
              :loading="loading"
              flat
              separator="horizontal"
            >
              <template v-slot:loading>
                <div class="hris-loading absolute-full flex flex-center" style="background: rgba(255,255,255,0.7); z-index: 1;">
                  <q-spinner color="green" size="40px" />
                  <div>Loading checklist tasks...</div>
                </div>
              </template>
              <template v-slot:no-data>
                <div class="hris-empty full-width">No checklist tasks assigned yet.</div>
              </template>
              <template v-slot:body="props">
                <q-tr :props="props">
                  <q-td key="task_name" :props="props">
                    <div class="text-weight-bold">{{ props.row.task_name }}</div>
                  </q-td>
                  <q-td key="description" :props="props" style="white-space: pre-wrap;">
                    {{ props.row.description || 'No additional details.' }}
                  </q-td>
                  <q-td key="due_date" :props="props">{{ formatDate(props.row.due_date) }}</q-td>
                  <q-td key="status" :props="props">
                    <q-chip
                      :color="getStatusColor(props.row.status)"
                      text-color="white"
                      dense
                    >
                      {{ props.row.status.toUpperCase().replace('_', ' ') }}
                    </q-chip>
                  </q-td>
                  <q-td key="actions" :props="props" class="text-center">
                    <q-btn
                      v-if="['pending', 'in_progress'].includes(props.row.status)"
                      flat
                      dense
                      class="hris-q-ghost"
                      icon="fas fa-check"
                      label="Mark Completed"
                      @click="markCompleted(props.row)"
                    />
                    <div v-else class="text-caption text-grey">
                      Done ({{ formatDateTime(props.row.completed_at) }})
                    </div>
                  </q-td>
                </q-tr>
              </template>
            </q-table>
          </div>
        </div>

        <!-- SIDEBAR INFO (ORIENTATIONS & CONTRACTS) -->
        <div class="col-12 col-md-5">
          <div class="column q-gutter-y-md">
            <!-- ORIENTATIONS -->
            <div class="hris-panel">
              <h3 class="hris-panel-title">
                <i class="fas fa-calendar-alt q-mr-sm"></i>My Orientations
              </h3>
              <q-list separator v-if="orientations.length > 0">
                <q-item v-for="ori in orientations" :key="ori.id" class="q-py-md">
                  <q-item-section avatar>
                    <q-avatar icon="fas fa-clock" color="green-1" text-color="green" />
                  </q-item-section>
                  <q-item-section>
                    <q-item-label class="text-weight-bold">{{ ori.title }}</q-item-label>
                    <q-item-label caption>Scheduled: {{ formatDateTime(ori.date_scheduled) }}</q-item-label>
                    <q-item-label caption>Venue: {{ ori.location_details }}</q-item-label>
                    <q-item-label caption v-if="ori.host">
                      Host: {{ ori.host.firstname }} {{ ori.host.lastname }}
                    </q-item-label>
                  </q-item-section>
                  <q-item-section side>
                    <q-chip dense :color="getOrientationStatusColor(ori.status)" text-color="white">
                      {{ ori.status }}
                    </q-chip>
                  </q-item-section>
                </q-item>
              </q-list>
              <div v-else class="hris-empty">No orientation schedules set up yet.</div>
            </div>

            <!-- CONTRACTS -->
            <div class="hris-panel">
              <h3 class="hris-panel-title">
                <i class="fas fa-file-signature q-mr-sm"></i>My Contract Status
              </h3>
              <q-list separator v-if="contracts.length > 0">
                <q-item v-for="ctr in contracts" :key="ctr.id" class="q-py-md">
                  <q-item-section avatar>
                    <q-avatar icon="fas fa-file-contract" color="green-1" text-color="green" />
                  </q-item-section>
                  <q-item-section>
                    <q-item-label class="text-weight-bold">{{ ctr.template_name }}</q-item-label>
                    <q-item-label caption>Generated: {{ formatDateTime(ctr.generated_at) }}</q-item-label>
                    <q-item-label caption v-if="ctr.signed_at">Signed: {{ formatDateTime(ctr.signed_at) }}</q-item-label>
                  </q-item-section>
                  <q-item-section side>
                    <q-chip dense :color="getContractStatusColor(ctr.status)" text-color="white">
                      {{ ctr.status }}
                    </q-chip>
                  </q-item-section>
                  <q-item-section side v-if="ctr.status === 'sent'">
                    <q-btn
                      unelevated
                      class="hris-q-primary"
                      size="sm"
                      label="Sign"
                      @click="signContract(ctr)"
                    />
                  </q-item-section>
                </q-item>
              </q-list>
              <div v-else class="hris-empty">No contract records issued.</div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import axios from "axios";
import mixins from "../../mixins";

export default {
  mixins: [mixins],
  data() {
    return {
      checklists: [],
      orientations: [],
      contracts: [],
      loading: false,
      columns: [
        { name: "task_name", align: "left", label: "Task", sortable: true },
        { name: "description", align: "left", label: "Description" },
        { name: "due_date", align: "center", label: "Due Date", sortable: true },
        { name: "status", align: "center", label: "Status", sortable: true },
        { name: "actions", align: "center", label: "Action" }
      ]
    };
  },
  created() {
    this.getMyOnboardingData();
  },
  methods: {
    getHeaders() {
      return {
        headers: { Authorization: "Bearer " + this.token }
      };
    },
    getMyOnboardingData() {
      this.loading = true;
      const headers = this.getHeaders();
      
      // Load checklists
      axios.get(this.apiUrl + "/onboarding/checklists", headers).then((res) => {
        if (res.data.status) {
          this.checklists = res.data.data;
        }
      }).finally(() => {
        this.loading = false;
      });

      // Load orientations
      axios.get(this.apiUrl + "/onboarding/orientations", headers).then((res) => {
        if (res.data.status) {
          this.orientations = res.data.data;
        }
      });

      // Load contracts
      axios.get(this.apiUrl + "/onboarding/contracts", headers).then((res) => {
        if (res.data.status) {
          this.contracts = res.data.data;
        }
      });
    },
    getStatusColor(status) {
      switch (status) {
        case "completed":
          return "positive";
        case "in_progress":
          return "warning";
        case "waived":
          return "grey";
        default:
          return "negative";
      }
    },
    getOrientationStatusColor(status) {
      switch (status) {
        case "completed":
          return "positive";
        case "cancelled":
          return "red-5";
        case "no_show":
          return "red";
        default:
          return "primary";
      }
    },
    getContractStatusColor(status) {
      switch (status) {
        case "signed":
          return "positive";
        case "sent":
          return "blue";
        case "void":
          return "grey";
        default:
          return "orange";
      }
    },
    markCompleted(row) {
      axios
        .post(
          this.apiUrl + "/onboarding/checklist/update",
          { id: row.id, status: "completed" },
          this.getHeaders()
        )
        .then((res) => {
          if (res.data.status) {
            this.$q.notify({ type: "positive", message: "Task completed successfully." });
            this.getMyOnboardingData();
          }
        });
    },
    signContract(row) {
      this.$q
        .dialog({
          title: "Sign Employment Contract",
          message: `Do you acknowledge and agree to sign "${row.template_name}"?`,
          cancel: true,
          persistent: true
        })
        .onOk(() => {
          axios
            .post(
              this.apiUrl + "/onboarding/contract/update",
              { id: row.id, status: "signed" },
              this.getHeaders()
            )
            .then((res) => {
              if (res.data.status) {
                this.$q.notify({ type: "positive", message: "Contract signed successfully!" });
                this.getMyOnboardingData();
              }
            });
        });
    }
  }
};
</script>

<style scoped>
.full-height {
  height: 100%;
}
</style>
